/**
 * Auth state is derived from GET /api/auth/me (session cookie).
 * The JWT lives only in an HttpOnly cookie set by the server — not in localStorage or JS-accessible storage.
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { normalizeSessionUser } from '../utils/sessionUser';
import { getRememberLoginPreference } from '../utils/rememberLogin';
import { withTimeout } from '../lib/withTimeout';

const AuthContext = createContext(null);

/** Old builds stored JWT / user JSON here — never write auth back to localStorage. */
const LEGACY_TOKEN_KEY = 'stavyn_token';
const LEGACY_USER_KEY = 'stavyn_user';

/** Renew access cookie before default 15m JWT expiry (remember-me users). */
const PROACTIVE_REFRESH_MS = 12 * 60 * 1000;

/** Never block login/signup UI if session restore stalls. */
const SESSION_BOOT_TIMEOUT_MS = 15_000;

function purgeLegacyAuthStorage() {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  } catch {
    // ignore
  }
}

function mapProfileToUser(profile) {
  return normalizeSessionUser(profile);
}

async function restoreSessionFromCookies({ preferRefreshFirst = false } = {}) {
  if (preferRefreshFirst) {
    await authAPI.refresh().catch(() => {});
  }
  try {
    const profile = await authAPI.getProfile();
    return mapProfileToUser(profile);
  } catch {
    if (!preferRefreshFirst) {
      await authAPI.refresh().catch(() => {});
      try {
        const profile = await authAPI.getProfile();
        return mapProfileToUser(profile);
      } catch {
        return null;
      }
    }
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const proactiveRefreshRef = useRef(null);

  const syncSessionFromServer = useCallback(async () => {
    try {
      const profile = await authAPI.getProfile();
      setUser(mapProfileToUser(profile));
      return true;
    } catch {
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    purgeLegacyAuthStorage();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const preferRefreshFirst = getRememberLoginPreference();
        const restored = await withTimeout(
          restoreSessionFromCookies({ preferRefreshFirst }),
          SESSION_BOOT_TIMEOUT_MS,
          'Session restore timed out'
        ).catch(() => null);
        if (!cancelled) {
          setUser(restored);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (proactiveRefreshRef.current) {
      clearInterval(proactiveRefreshRef.current);
      proactiveRefreshRef.current = null;
    }
    if (!user || !getRememberLoginPreference()) return undefined;

    const renew = () => {
      authAPI.refresh().catch(() => {});
    };

    proactiveRefreshRef.current = setInterval(renew, PROACTIVE_REFRESH_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') renew();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (proactiveRefreshRef.current) {
        clearInterval(proactiveRefreshRef.current);
        proactiveRefreshRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  const login = useCallback(async (email, password, inviteToken, rememberMe = false) => {
    setError(null);
    try {
      const data = await authAPI.login(email, password, inviteToken, rememberMe);
      const userData = normalizeSessionUser({
        ...data.user,
        name: data.user?.name || email,
      });
      setUser(userData);
      setSessionReady(true);
      return data;
    } catch (err) {
      const message = err?.message || 'Invalid email or password';
      setError(message);
      throw err;
    }
  }, []);

  const signup = useCallback(async (name, email, password, inviteToken) => {
    setError(null);
    try {
      const data = await authAPI.signup(name, email, password, inviteToken);
      const userData = normalizeSessionUser({
        ...data.user,
        name: data.user?.name || name,
      });
      setUser(userData);
      setSessionReady(true);
      return data;
    } catch (err) {
      const message = err?.message || 'Could not create account';
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Still clear client state if the network call fails.
    }
    purgeLegacyAuthStorage();
    setUser(null);
    setSessionReady(true);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser((prev) => normalizeSessionUser({ ...prev, ...userData }));
  }, []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        sessionReady,
        login,
        signup,
        logout,
        updateUser,
        syncSessionFromServer,
        error,
        setError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
